import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Graph from "./Graphs";
import TableHWI from "./Table";
import { useGetDSMMetersQuery } from "../operatorDashboardAPISlice";

const popperSx = {
  "& .MuiPaper-root": { backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" },
  "& .MuiPickersDay-root": { color: "#e0e6f0" },
  "& .MuiPickersDay-root.Mui-selected": { backgroundColor: "#FDB940", color: "#0d1117" },
  "& .MuiPickersDay-root:hover": { backgroundColor: "rgba(253,185,64,0.15)" },
  "& .MuiDayCalendar-weekDayLabel": { color: "rgba(255,255,255,0.45)" },
  "& .MuiPickersCalendarHeader-label": { color: "#fff" },
  "& .MuiIconButton-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiMultiSectionDigitalClock-root": { color: "#fff" },
  "& .MuiMenuItem-root": { color: "#e0e6f0" },
  "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "rgba(253,185,64,0.2)", color: "#FDB940" },
};

const datePickerInputSx = {
  "& .MuiInputBase-root": { backgroundColor: "rgba(255,255,255,0.04)", borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#FDB940" },
};

export default function HistoricalDataPage() {
  const [customStartDate, setCustomStartDate] = useState(null);
  const [useCustomWindow, setUseCustomWindow] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(dayjs().subtract(2, "hour"));

  const handleApply = () => {
    setCustomStartDate(selectedDateTime);
    setUseCustomWindow(true);
    setOpen(false);
  };

  const handleResetToLive = () => {
    setCustomStartDate(null);
    setUseCustomWindow(false);
    setOpen(false);
  };

  return (
    <Box sx={{ margin: "0 auto", padding: "70px 32px 40px", maxWidth: "1700px" }}>
      <PageHeader />

      {/* Graphs */}
      <Box sx={{ mt: "20px" }}>
        <Graph startDate={useCustomWindow ? customStartDate : null} />
      </Box>

      {/* Historical window button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "16px", mb: "16px" }}>
        <Button
          variant="outlined"
          startIcon={<CalendarTodayIcon sx={{ fontSize: "0.9rem" }} />}
          onClick={() => setOpen(true)}
          sx={{
            color: "#FDB940",
            borderColor: "rgba(253,185,64,0.45)",
            fontSize: "0.82rem",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": { borderColor: "#FDB940", bgcolor: "rgba(253,185,64,0.06)" },
          }}
        >
          {useCustomWindow ? "Change Time Window" : "View Historical Window"}
        </Button>
      </Box>

      {/* Table */}
      <TableHWI startDate={useCustomWindow ? customStartDate : null} />

      {/* DateTime picker dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", minWidth: 340 } }}
      >
        <DialogTitle sx={{ color: "#fff", fontSize: "1rem", fontWeight: 600 }}>
          Select 2-Hour Time Window
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: "4px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Start Time"
                value={selectedDateTime}
                onChange={(v) => v && setSelectedDateTime(v)}
                maxDateTime={dayjs()}
                format="DD/MM/YYYY HH:mm"
                ampm={false}
                sx={datePickerInputSx}
                slotProps={{ popper: { sx: popperSx } }}
              />
            </LocalizationProvider>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)" }}>
              Data from {selectedDateTime.format("DD/MM/YYYY HH:mm")} to{" "}
              {selectedDateTime.add(2, "hour").format("DD/MM/YYYY HH:mm")}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(253,185,64,0.7)" }}>
              Auto-refresh will be disabled for historical windows.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: "4px" }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none", "&:hover": { color: "#fff" } }}>
            Cancel
          </Button>
          {useCustomWindow && (
            <Button onClick={handleResetToLive} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none", "&:hover": { color: "#fff" } }}>
              Reset to Live
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ backgroundColor: "#FDB940", color: "#0d1117", fontWeight: 700, textTransform: "none", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" } }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PageHeader() {
  const { serial_number, meter_id } = useParams();
  const navigate = useNavigate();

  const { data: metersData } = useGetDSMMetersQuery(
    { searchQuery: serial_number, pagesize: 1 },
    { skip: !serial_number }
  );
  const device = metersData?.data?.[0];
  const maxPower = device?.latest_power_rating;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", pt: "28px", mb: "24px" }}>
      <ArrowBackIcon
        sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", flexShrink: 0, "&:hover": { color: "#fff" } }}
        onClick={() => navigate(-1)}
      />
      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: "12px" }}>
        <Typography sx={{ fontSize: "1.3rem", fontWeight: 600, color: "#fff" }}>
          Tjommie SN ·{" "}
          <Box component="span" sx={{ color: "#fff", fontFamily: "monospace" }}>
            {serial_number}
          </Box>
        </Typography>
        {maxPower != null && (
          <Tooltip
            title={
              <Box>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", mb: "4px" }}>Max Power (kW)</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)" }}>
                  Highest instantaneous draw the device has ever reported. Surfaced on the Reliability dashboard as well.
                </Typography>
              </Box>
            }
            placement="bottom-start"
            componentsProps={{ tooltip: { sx: { bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px", p: "10px 12px", maxWidth: 260 } } }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "6px", px: "10px", py: "4px", cursor: "default" }}>
              <Typography sx={{ fontSize: "0.82rem", color: "#e0e6f0", fontWeight: 500 }}>
                Max Power:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>{Number(maxPower).toFixed(2)} kW</Box>
              </Typography>
              <InfoOutlinedIcon sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.45)" }} />
            </Box>
          </Tooltip>
        )}
      </Box>
      <Button
        variant="outlined"
        // startIcon={<CalendarTodayIcon sx={{ fontSize: "0.9rem" }} />}
        onClick={() => navigate(`/operator/historical-data/${serial_number}/${meter_id}/rawdata/`)}
        sx={{
          color: "#FDB940",
          borderColor: "rgba(253,185,64,0.45)",
          fontSize: "0.82rem",
          fontWeight: 500,
          textTransform: "none",
          borderRadius: "8px",
          "&:hover": { borderColor: "#FDB940", bgcolor: "rgba(253,185,64,0.06)" },
        }}
      >
        Raw Data
      </Button>
    </Box>
  );
}
